import { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Upload, X, Phone, Mail, MessageCircle, CheckCircle2, Calculator } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "../components/Seo";
import { MaskedLines, FadeUp, Eyebrow } from "../components/Reveal";
import { useSettings } from "../lib/SettingsContext";
import { api, formatApiErrorDetail } from "../lib/api";

const SERVICE_OPTIONS = [
  {
    id: "boiler",
    value: "Pulizia e decalcificazione boiler",
    desc: "Rimozione calcare, controllo componenti e rapporto finale documentato.",
    price: "da CHF 390",
  },
  {
    id: "serpentine",
    value: "Lavaggio circuiti di riscaldamento a pavimento",
    desc: "Lavaggio dei circuiti con verifica delle condizioni operative.",
    price: "CHF 80 / circuito · min. CHF 390",
  },
  {
    id: "filtri",
    value: "Manutenzione filtri acqua sanitaria",
    desc: "Sostituzione cartuccia standard e controllo finale.",
    price: "CHF 150 · filtro compreso",
  },
];

const CLIENT_TYPES = ["Privato", "Amministrazione immobiliare", "Azienda"];
const BOILER_RANGES = ["Fino a 300 litri", "Da 301 a 500 litri", "Da 501 a 800 litri", "Oltre 800 litri", "Non lo so"];
const CONTACT_PREFS = [
  { value: "telefono", label: "Telefono" },
  { value: "email", label: "Email" },
  { value: "whatsapp", label: "WhatsApp" },
];

const STEPS = ["Il servizio", "L'impianto", "Contatti"];

const inputCls =
  "w-full bg-white border border-ink/15 px-4 py-3.5 text-sm text-ink placeholder:text-ink/35 focus:outline-none focus:border-bronzo transition-colors duration-300";
const labelCls = "block font-mono text-[11px] uppercase tracking-[0.18em] text-ink/60 mb-2";

const initialData = {
  service: "",
  service_id: "",
  client_type: "",
  comune: "",
  cap: "",
  address: "",
  boiler_liters: "",
  boiler_model: "",
  circuits: "",
  problems: "",
  filter_model: "",
  description: "",
  name: "",
  email: "",
  phone: "",
  contact_preference: "telefono",
  privacy: false,
  website: "",
};

function computeEstimate(d) {
  if (d.service_id === "boiler") {
    if (!d.boiler_liters || d.boiler_liters === "Non lo so") return null;
    if (d.boiler_liters === "Fino a 300 litri") return { text: "CHF 390", kind: "final" };
    if (d.boiler_liters === "Da 301 a 500 litri") return { text: "CHF 590", kind: "final" };
    if (d.boiler_liters === "Da 501 a 800 litri") return { text: "CHF 790", kind: "final" };
    return { text: "da CHF 890 (CHF 1.10 per litro)", kind: "estimate" };
  }
  if (d.service_id === "serpentine") {
    const n = parseInt(d.circuits, 10);
    if (!n || n <= 0) return null;
    return { text: `CHF ${Math.max(390, n * 80).toLocaleString("it-CH")}`, kind: "final", extra: n * 80 < 390 ? "Minimo d'intervento CHF 390" : `${n} circuiti × CHF 80` };
  }
  if (d.service_id === "filtri") {
    if (d.client_type === "Amministrazione immobiliare") return null;
    return { text: "CHF 150", kind: "final", extra: "Filtro standard compreso" };
  }
  return null;
}

export default function ContattiPage() {
  const { settings } = useSettings();
  const [searchParams] = useSearchParams();
  const preService = searchParams.get("servizio") || "";
  const preClient = searchParams.get("cliente") || "";
  const pre = SERVICE_OPTIONS.find((s) => s.id === preService);

  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    ...initialData,
    service: pre?.value || "",
    service_id: pre?.id || "",
    client_type: preClient === "amministrazione" ? "Amministrazione immobiliare" : "",
  });
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const estimate = computeEstimate(data);

  const validStep = () => {
    if (step === 0) return !!data.service;
    if (step === 1) return true;
    if (step === 2)
      return (
        !!data.name.trim() &&
        /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(data.email) &&
        !!data.phone.trim() &&
        !!data.comune.trim() &&
        !!data.client_type &&
        data.privacy
      );
    return false;
  };

  const addPhotos = (files) => setPhotos((p) => [...p, ...Array.from(files)].slice(0, 5));

  const submit = async () => {
    if (!validStep() || submitting) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      const { service_id, ...rest } = data;
      Object.entries(rest).forEach(([k, v]) => fd.append(k, String(v)));
      fd.append("price_estimate", estimate ? estimate.text : "");
      photos.forEach((p) => fd.append("photos", p));
      await api.post("/requests", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "instant" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const EstimateBox = () => (
    <div className="sm:col-span-2 border border-bronzo/40 bg-beige p-6" data-testid="price-estimate-box">
      <div className="flex items-center gap-3 mb-2">
        <Calculator className="w-4 h-4 text-bronzo" />
        <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/60">Prezzo indicativo</p>
      </div>
      {estimate ? (
        <>
          <p className="font-display font-medium text-2xl text-antracite" data-testid="price-estimate-value">{estimate.text}</p>
          {estimate.extra && <p className="mt-1 text-xs text-ink/50">{estimate.extra}</p>}
          <p className="mt-3 text-xs text-ink/50 leading-relaxed">
            Prezzo finale per l'intervento standard descritto, confermato prima
            dell'esecuzione. Lavori aggiuntivi vengono concordati in anticipo.
          </p>
        </>
      ) : (
        <p className="text-sm text-ink/60 leading-relaxed" data-testid="price-estimate-pending">
          Il prezzo verrà confermato dopo la verifica dell'impianto
          {data.client_type === "Amministrazione immobiliare" ? " — per le amministrazioni prepariamo un preventivo dedicato" : ""}.
          {data.service_id === "boiler" && " Indica la capacità del boiler per una stima immediata."}
          {data.service_id === "serpentine" && " Indica il numero di circuiti per una stima immediata."}
        </p>
      )}
    </div>
  );

  return (
    <div data-testid="contatti-page">
      <Seo
        title="Richiedi un intervento | V32 Group — Manutenzioni Tecniche, Ticino"
        description="Richiedi pulizia boiler, lavaggio circuiti di riscaldamento a pavimento o manutenzione filtri acqua sanitaria in Ticino. Prezzo indicativo immediato e conferma prima dell'intervento."
      />

      <section className="pt-32 md:pt-40 pb-12">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Eyebrow>Contatti</Eyebrow>
          <MaskedLines
            lines={["Richiedi un intervento."]}
            delay={0.15}
            className="mt-6 font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] text-ink"
          />
          <FadeUp delay={0.45}>
            <p className="mt-6 text-ink/70 leading-relaxed max-w-2xl">
              Tre passaggi, pochi minuti. Le informazioni sull'impianto ci permettono
              di confermarti prezzo e disponibilità prima dell'intervento.
            </p>
          </FadeUp>
        </div>
      </section>

      {done ? (
        <section className="pb-24 lg:pb-32">
          <div className="max-w-3xl mx-auto px-6 sm:px-10">
            <FadeUp>
              <div className="border border-bronzo/40 bg-white p-10 lg:p-14 text-center" data-testid="form-confirmation">
                <CheckCircle2 className="w-12 h-12 text-bronzo mx-auto" />
                <h2 className="mt-6 font-display font-light text-3xl tracking-tight text-ink">
                  Richiesta ricevuta.
                </h2>
                <p className="mt-4 text-ink/70 leading-relaxed max-w-md mx-auto">
                  Grazie {data.name.split(" ")[0]}. Abbiamo registrato la tua richiesta
                  di <span className="font-medium text-ink">{data.service}</span>
                  {estimate ? <> — prezzo indicativo {estimate.text}</> : null} — e ti
                  abbiamo inviato una conferma via email. Ti ricontatteremo per
                  confermare prezzo e disponibilità prima dell'intervento.
                </p>
                {settings.phone && (
                  <a
                    href={`tel:${settings.phone.replace(/\s/g, "")}`}
                    data-testid="confirmation-phone"
                    className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-antracite border-b border-bronzo pb-1 hover:text-bronzo transition-colors duration-300"
                  >
                    <Phone className="w-4 h-4" /> {settings.phone}
                  </a>
                )}
              </div>
            </FadeUp>
          </div>
        </section>
      ) : (
        <section className="pb-24 lg:pb-32">
          <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
              <div className="lg:col-span-8">
                <div className="flex items-center gap-0 mb-10" data-testid="form-steps-indicator">
                  {STEPS.map((s, i) => (
                    <div key={s} className="flex items-center flex-1 last:flex-none">
                      <div className="flex items-center gap-3">
                        <span
                          className={`w-8 h-8 flex items-center justify-center font-mono text-xs border transition-colors duration-300 ${
                            i < step
                              ? "bg-bronzo border-bronzo text-white"
                              : i === step
                                ? "border-bronzo text-bronzo"
                                : "border-ink/20 text-ink/40"
                          }`}
                        >
                          {i < step ? <Check className="w-4 h-4" /> : `0${i + 1}`}
                        </span>
                        <span className={`hidden md:block text-xs font-medium uppercase tracking-wider ${i === step ? "text-ink" : "text-ink/40"}`}>
                          {s}
                        </span>
                      </div>
                      {i < STEPS.length - 1 && <span className="flex-1 h-px bg-ink/15 mx-3" aria-hidden="true" />}
                    </div>
                  ))}
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={step}
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -24 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {step === 0 && (
                      <fieldset data-testid="form-step-1">
                        <legend className="font-display font-medium text-xl text-ink mb-6">
                          Quale servizio ti serve?
                        </legend>
                        <div className="grid grid-cols-1 gap-4">
                          {SERVICE_OPTIONS.map((t, i) => (
                            <button
                              key={t.id}
                              type="button"
                              data-testid={`service-option-${t.id}`}
                              onClick={() => setData((d) => ({ ...d, service: t.value, service_id: t.id, boiler_liters: "", circuits: "", filter_model: "" }))}
                              className={`text-left p-6 border transition-colors duration-300 flex items-start gap-5 ${
                                data.service_id === t.id
                                  ? "border-bronzo bg-white"
                                  : "border-ink/15 bg-white/50 hover:border-bronzo/50"
                              }`}
                            >
                              <span className="font-mono text-xs text-bronzo tracking-[0.25em] mt-1 shrink-0">0{i + 1}</span>
                              <span className="flex-1">
                                <span className={`block font-medium text-sm ${data.service_id === t.id ? "text-antracite" : "text-ink"}`}>
                                  {t.value}
                                </span>
                                <span className="mt-1.5 block text-xs text-ink/55 leading-relaxed">{t.desc}</span>
                              </span>
                              <span className="font-display font-medium text-sm text-antracite whitespace-nowrap hidden sm:block">
                                {t.price}
                              </span>
                            </button>
                          ))}
                        </div>
                      </fieldset>
                    )}

                    {step === 1 && (
                      <fieldset data-testid="form-step-2">
                        <legend className="font-display font-medium text-xl text-ink mb-6">
                          Informazioni sull'impianto
                        </legend>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                          {data.service_id === "boiler" && (
                            <>
                              <div>
                                <label htmlFor="f-liters" className={labelCls}>Capacità del boiler</label>
                                <select id="f-liters" data-testid="select-boiler-liters" className={inputCls} value={data.boiler_liters} onChange={(e) => set("boiler_liters", e.target.value)}>
                                  <option value="">Seleziona…</option>
                                  {BOILER_RANGES.map((r) => (
                                    <option key={r} value={r}>{r}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label htmlFor="f-bmodel" className={labelCls}>Marca e modello (se conosciuti)</label>
                                <input id="f-bmodel" data-testid="input-boiler-model" className={inputCls} placeholder="Es. …" value={data.boiler_model} onChange={(e) => set("boiler_model", e.target.value)} />
                              </div>
                            </>
                          )}
                          {data.service_id === "serpentine" && (
                            <>
                              <div>
                                <label htmlFor="f-circuits" className={labelCls}>Numero di circuiti</label>
                                <input id="f-circuits" data-testid="input-circuits" type="number" min="1" className={inputCls} placeholder="Es. 10" value={data.circuits} onChange={(e) => set("circuits", e.target.value)} />
                                <p className="mt-2 text-xs text-ink/40">Trovi il numero sul collettore di distribuzione.</p>
                              </div>
                              <div>
                                <label htmlFor="f-problems" className={labelCls}>Problemi riscontrati (facoltativo)</label>
                                <input id="f-problems" data-testid="input-problems" className={inputCls} placeholder="Es. alcuni circuiti tiepidi" value={data.problems} onChange={(e) => set("problems", e.target.value)} />
                              </div>
                            </>
                          )}
                          {data.service_id === "filtri" && (
                            <div className="sm:col-span-2">
                              <label htmlFor="f-fmodel" className={labelCls}>Marca e modello del filtro (se conosciuti)</label>
                              <input id="f-fmodel" data-testid="input-filter-model" className={inputCls} placeholder="Es. …" value={data.filter_model} onChange={(e) => set("filter_model", e.target.value)} />
                              <p className="mt-2 text-xs text-ink/40">
                                Al passaggio successivo puoi allegare una fotografia del filtro: ci aiuta a verificare la compatibilità del ricambio.
                              </p>
                            </div>
                          )}
                          <div>
                            <label htmlFor="f-client" className={labelCls}>Chi richiede l'intervento *</label>
                            <select id="f-client" data-testid="select-client-type" className={inputCls} value={data.client_type} onChange={(e) => set("client_type", e.target.value)}>
                              <option value="">Seleziona…</option>
                              {CLIENT_TYPES.map((c) => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                          </div>
                          <EstimateBox />
                        </div>
                      </fieldset>
                    )}

                    {step === 2 && (
                      <fieldset data-testid="form-step-3">
                        <legend className="font-display font-medium text-xl text-ink mb-6">
                          I tuoi contatti
                        </legend>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                          <div>
                            <label htmlFor="f-name" className={labelCls}>Nome e cognome / ragione sociale *</label>
                            <input id="f-name" data-testid="input-name" className={inputCls} autoComplete="name" value={data.name} onChange={(e) => set("name", e.target.value)} />
                          </div>
                          <div>
                            <label htmlFor="f-phone" className={labelCls}>Telefono *</label>
                            <input id="f-phone" data-testid="input-phone" type="tel" className={inputCls} autoComplete="tel" placeholder="+41 …" value={data.phone} onChange={(e) => set("phone", e.target.value)} />
                          </div>
                          <div className="sm:col-span-2">
                            <label htmlFor="f-email" className={labelCls}>Email *</label>
                            <input id="f-email" data-testid="input-email" type="email" className={inputCls} autoComplete="email" value={data.email} onChange={(e) => set("email", e.target.value)} />
                          </div>
                          <div>
                            <label htmlFor="f-comune" className={labelCls}>Comune *</label>
                            <input id="f-comune" data-testid="input-comune" className={inputCls} placeholder="Es. Mendrisio" value={data.comune} onChange={(e) => set("comune", e.target.value)} />
                          </div>
                          <div>
                            <label htmlFor="f-cap" className={labelCls}>CAP</label>
                            <input id="f-cap" data-testid="input-cap" className={inputCls} placeholder="Es. 6850" inputMode="numeric" value={data.cap} onChange={(e) => set("cap", e.target.value)} />
                          </div>
                          <div className="sm:col-span-2">
                            <label htmlFor="f-address" className={labelCls}>Indirizzo dell'intervento</label>
                            <input id="f-address" data-testid="input-address" className={inputCls} placeholder="Via, numero" autoComplete="street-address" value={data.address} onChange={(e) => set("address", e.target.value)} />
                          </div>
                          <div className="sm:col-span-2">
                            <label htmlFor="f-desc" className={labelCls}>Note aggiuntive</label>
                            <textarea id="f-desc" data-testid="textarea-description" rows={4} className={inputCls} placeholder="Accessibilità dell'impianto, disponibilità orarie, particolarità…" value={data.description} onChange={(e) => set("description", e.target.value)} />
                          </div>
                          <div className="sm:col-span-2">
                            <span className={labelCls}>Fotografie dell'impianto (facoltative, max 5)</span>
                            <button
                              type="button"
                              data-testid="photo-upload-button"
                              onClick={() => fileRef.current?.click()}
                              className="w-full border border-dashed border-ink/25 hover:border-bronzo bg-white/50 px-6 py-8 text-sm text-ink/60 hover:text-bronzo transition-colors duration-300 flex flex-col items-center gap-2"
                            >
                              <Upload className="w-5 h-5" />
                              Carica fotografie (JPG, PNG, WebP · max 5 MB ciascuna)
                            </button>
                            <input
                              ref={fileRef}
                              type="file"
                              accept="image/jpeg,image/png,image/webp,image/heic"
                              multiple
                              className="hidden"
                              data-testid="photo-upload-input"
                              onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }}
                            />
                            {photos.length > 0 && (
                              <ul className="mt-3 flex flex-wrap gap-2" data-testid="photo-list">
                                {photos.map((p, i) => (
                                  <li key={i} className="inline-flex items-center gap-2 bg-beige px-3 py-1.5 text-xs text-ink/70">
                                    {p.name}
                                    <button type="button" aria-label={`Rimuovi ${p.name}`} onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="text-ink/40 hover:text-bronzo">
                                      <X className="w-3.5 h-3.5" />
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                          <div className="sm:col-span-2">
                            <span className={labelCls}>Preferenza di contatto</span>
                            <div className="flex flex-wrap gap-3">
                              {CONTACT_PREFS.map((c) => (
                                <button
                                  key={c.value}
                                  type="button"
                                  data-testid={`contact-pref-${c.value}`}
                                  onClick={() => set("contact_preference", c.value)}
                                  className={`px-5 py-2.5 text-sm border transition-colors duration-300 ${
                                    data.contact_preference === c.value
                                      ? "border-bronzo bg-white text-antracite"
                                      : "border-ink/15 text-ink/60 hover:border-bronzo/50"
                                  }`}
                                >
                                  {c.label}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div className="sm:col-span-2">
                            <label className="flex items-start gap-3 cursor-pointer" data-testid="privacy-consent">
                              <input
                                type="checkbox"
                                checked={data.privacy}
                                onChange={(e) => set("privacy", e.target.checked)}
                                className="mt-1 w-4 h-4 accent-[#B18C69]"
                                data-testid="checkbox-privacy"
                              />
                              <span className="text-xs text-ink/60 leading-relaxed">
                                Ho letto l'<a href="/privacy" target="_blank" className="underline decoration-bronzo underline-offset-2">informativa privacy</a> e
                                acconsento al trattamento dei miei dati per essere
                                ricontattato in merito alla mia richiesta. *
                              </span>
                            </label>
                          </div>
                        </div>
                      </fieldset>
                    )}
                  </motion.div>
                </AnimatePresence>

                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute opacity-0 h-0 w-0 pointer-events-none"
                  style={{ position: "absolute", left: "-9999px" }}
                  value={data.website}
                  onChange={(e) => set("website", e.target.value)}
                />

                <div className="mt-10 flex items-center justify-between gap-4">
                  <button
                    type="button"
                    data-testid="form-back-button"
                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                    disabled={step === 0}
                    className="inline-flex items-center gap-2 text-sm font-medium text-ink/60 hover:text-bronzo disabled:opacity-0 transition-colors duration-300"
                  >
                    <ArrowLeft className="w-4 h-4" /> Indietro
                  </button>
                  {step < 2 ? (
                    <button
                      type="button"
                      data-testid="form-next-button"
                      onClick={() => validStep() && setStep((s) => s + 1)}
                      disabled={!validStep()}
                      className="inline-flex items-center gap-2 bg-antracite text-avorio font-medium px-8 py-3.5 hover:bg-bronzo disabled:opacity-40 disabled:hover:bg-antracite transition-colors duration-300"
                    >
                      Continua <ArrowRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      data-testid="form-submit-button"
                      onClick={submit}
                      disabled={!validStep() || submitting}
                      className="inline-flex items-center gap-2 bg-bronzo text-white font-medium px-8 py-3.5 hover:bg-antracite disabled:opacity-40 transition-colors duration-300"
                    >
                      {submitting ? "Invio in corso…" : "Invia la richiesta"} <ArrowRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              <aside className="lg:col-span-4">
                <FadeUp delay={0.2}>
                  <div className="bg-antracite text-avorio p-8 lg:p-10 space-y-8 lg:sticky lg:top-28" data-testid="contact-aside">
                    <div>
                      <p className="font-mono text-xs uppercase tracking-[0.25em] text-bronzo mb-4">
                        Preferisci parlare subito?
                      </p>
                      {settings.phone && (
                        <a
                          href={`tel:${settings.phone.replace(/\s/g, "")}`}
                          data-testid="aside-phone"
                          className="inline-flex items-center gap-3 text-lg font-display hover:text-bronzo transition-colors duration-300"
                        >
                          <Phone className="w-5 h-5 text-bronzo" /> {settings.phone}
                        </a>
                      )}
                      {settings.email && (
                        <a href={`mailto:${settings.email}`} data-testid="aside-email" className="mt-4 inline-flex items-center gap-3 text-sm text-avorio/70 hover:text-bronzo transition-colors duration-300">
                          <Mail className="w-4 h-4 text-bronzo" /> {settings.email}
                        </a>
                      )}
                      {settings.whatsapp && (
                        <a
                          href={`https://wa.me/${settings.whatsapp.replace(/[^0-9]/g, "")}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="aside-whatsapp"
                          className="mt-4 inline-flex items-center gap-3 text-sm text-avorio/70 hover:text-bronzo transition-colors duration-300"
                        >
                          <MessageCircle className="w-4 h-4 text-bronzo" /> WhatsApp Business
                        </a>
                      )}
                    </div>
                    <div className="border-t border-avorio/10 pt-8">
                      <p className="font-mono text-xs uppercase tracking-[0.25em] text-bronzo mb-4">Territorio</p>
                      <p className="text-sm text-avorio/70 leading-relaxed">
                        Operiamo in tutto il Canton Ticino, con partenza dal
                        Mendrisiotto.
                      </p>
                    </div>
                    <div className="border-t border-avorio/10 pt-8">
                      <p className="font-mono text-xs uppercase tracking-[0.25em] text-bronzo mb-4">Cosa succede dopo</p>
                      <ol className="space-y-3 text-sm text-avorio/70">
                        <li className="flex gap-3"><span className="text-bronzo font-mono text-xs mt-0.5">01</span> Esaminiamo la tua richiesta</li>
                        <li className="flex gap-3"><span className="text-bronzo font-mono text-xs mt-0.5">02</span> Confermiamo prezzo e disponibilità</li>
                        <li className="flex gap-3"><span className="text-bronzo font-mono text-xs mt-0.5">03</span> Concordiamo la data dell'intervento</li>
                      </ol>
                    </div>
                  </div>
                </FadeUp>
              </aside>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
